import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../services/AuthContext';
import { useNotes } from "../services/NotesContext";
import { useBesoins } from "../services/BesoinsContext";
import { fetchWithAuth } from '../services/AuthService';
import { decryptWithUserKey } from '../services/EncryptNotes';
import { COLOR_CATEGORIES } from "../constants/colorCategories";
import QuizResponse from './QuizResponse';
import NoteCard from './NoteCard';
import BesoinCard from './BesoinCard';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import '../css/Result.css';
import '../css/Note.css';
import '../css/Besoin.css';

// Nombre d'éléments demandés au backend à chaque page
const PAGE_SIZE = 10;

const History = () => {
  const { t } = useTranslation();
  const { isUserAuthenticated, authLoading } = useAuth();
  const { checkAndLoadNotes, getKey } = useNotes();
  const { checkAndLoadBesoins } = useBesoins();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);          // premier chargement
  const [loadingMore, setLoadingMore] = useState(false); // pages suivantes
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  const [isLinkedNotesModalOpen, setIsLinkedNotesModalOpen] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [selectedScan, setSelectedScan] = useState(null);

  const containerRef = useRef(null);
  const sentinelRef = useRef(null);

  // Curseur de pagination { before, beforeId } renvoyé par le backend
  const cursorRef = useRef(null);
  const hasMoreRef = useRef(true);
  const isFetchingRef = useRef(false);

  // --- Déchiffre le contenu des notes (title/text) et besoins (text/keywords) reçues du backend ---
  const decryptItems = useCallback(async (rawItems) => {
    const hasEncryptedItems = rawItems.some((item) => item.type === 'note' || item.type === 'besoin');
    if (!hasEncryptedItems) return rawItems;

    let key = null;
    try {
      key = await getKey();
    } catch (err) {
      console.error("Impossible de récupérer la clé utilisateur :", err);
    }
    if (!key) return rawItems;

    return Promise.all(
      rawItems.map(async (item) => {
        if (item.type === 'note') {
          try {
            const title = await decryptWithUserKey(item.data.title, key);
            const text = await decryptWithUserKey(item.data.text, key);
            return { ...item, data: { ...item.data, title, text } };
          } catch (err) {
            console.error("Erreur de décryptage pour la note :", item._id, err);
            return item;
          }
        } else if (item.type === 'besoin') {
          try {
            const text = await decryptWithUserKey(item.data.text, key);
            const keywords = await Promise.all(
              (item.data.keywords || []).map(kw => decryptWithUserKey(kw, key))
            );
            return { ...item, data: { ...item.data, text, keywords } };
          } catch (err) {
            console.error("Erreur de décryptage pour le besoin :", item._id, err);
            return item;
          }
        }
        return item;
      })
    );
  }, [getKey]);

  // --- Chargement d'une page (reset = première page) ---
  const loadPage = useCallback(async (reset = false) => {
    if (isFetchingRef.current) return;
    if (!reset && !hasMoreRef.current) return;

    isFetchingRef.current = true;
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      const cursor = reset ? null : cursorRef.current;
      if (cursor) {
        params.set('before', cursor.before);
        params.set('beforeId', cursor.beforeId);
      }

      const response = await fetchWithAuth(`/api/history?${params.toString()}`);
      if (!response.ok) throw new Error("Erreur lors du chargement de l'historique.");

      const data = await response.json();
      const pageItems = await decryptItems(data.items || []);

      setItems((prev) => {
        const merged = reset ? pageItems : [...prev, ...pageItems];
        // Sécurité anti-doublon (double déclenchement du scroll, etc.)
        const unique = new Map(merged.map((it) => [`${it.type}-${it._id}`, it]));
        return Array.from(unique.values());
      });

      cursorRef.current = data.nextCursor || null;
      hasMoreRef.current = !!data.hasMore;
      setHasMore(!!data.hasMore);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Impossible de charger l'historique.");
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
  }, [decryptItems]);

  // --- Chargement initial ---
  useEffect(() => {
    if (authLoading) return;

    if (!isUserAuthenticated) {
      setLoading(false);
      setError("Vous devez être connecté pour voir l'historique.");
      return;
    }

    const init = async () => {
      // Les notes et besoins du contexte servent aux "liens" affichés sur les scans
      await checkAndLoadNotes();
      await checkAndLoadBesoins();
      cursorRef.current = null;
      hasMoreRef.current = true;
      setHasMore(true);
      await loadPage(true);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated, authLoading]);

  // --- Chargement dynamique au scroll (sentinelle en bas de liste) ---
  useEffect(() => {
    const root = containerRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadPage(false);
        }
      },
      { root, rootMargin: '200px', threshold: 0 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [loadPage, hasMore, items.length]);

  // --- Fonction appelée depuis QuizResponse ---
  const handleOpenLinkedNotes = async (scanId, linkedNotes) => {
    setSelectedScan(scanId);
    setSelectedNotes(linkedNotes);
    setIsLinkedNotesModalOpen(true);
  };

  const renderItem = (item) => {
    switch (item.type) {
      case 'quiz':
        return (
          <QuizResponse
            scanId={item.data._id}
            scoringValue={item.data.scoring}
            answers={item.data.answers}
            createdAt={item.data.createdAt}
            from="history"
            onOpenLinkedNotes={handleOpenLinkedNotes}
          />
        );
      case 'note':
        return (
          <NoteCard
            note={item.data}
            colorCategories={COLOR_CATEGORIES}
            onEdit={null}
            onDelete={null}
          />
        );
      case 'besoin':
        return (
          <BesoinCard
            besoin={item.data}
            onEdit={null}
            onDelete={null}
          />
        );
      default:
        return null;
    }
  };

  if (authLoading) return <p>Vérification de la session...</p>;
  if (loading) return <p>Chargement...</p>;
  if (error) return <p>{error}</p>;
  if (items.length === 0) {
    return <p>{t('history.empty', { defaultValue: 'Aucun élément enregistré.' })}</p>;
  }

  return (
    <>
        <div style={{ marginTop: '5px'}}></div>

      <div ref={containerRef} style={{ maxHeight: '90vh', overflowY: 'auto'}}>
        {items.map((item) => (
          <div key={`${item.type}-${item._id}`} className={`history-item history-item-${item.type}`}>
            {/* <div className="history-item-type">
              {t(`history.types.${item.type}`, {
                defaultValue:
                  item.type === 'quiz' ? 'Scan' : item.type === 'note' ? 'Note' : 'Besoin',
              })}
            </div> */}
            {renderItem(item)}
          </div>
        ))}

        {/* Sentinelle : son apparition à l'écran déclenche la page suivante */}
        {hasMore && (
          <div ref={sentinelRef} style={{ padding: '12px', textAlign: 'center', color: '#aaa' }}>
            {loadingMore ? t('history.loadingMore', { defaultValue: 'Chargement...' }) : '\u00A0'}
          </div>
        )}
      </div>

      {/* --- MODAL GLOBALE : Notes liées --- */}
      <Modal
        show={isLinkedNotesModalOpen}
        onHide={() => setIsLinkedNotesModalOpen(false)}
        centered
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>{t('history.note.header')}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedNotes && selectedNotes.length > 0 ? (
            <div className="notes-list">
              {selectedNotes.map((note) => (
                <NoteCard
                  key={note._id}
                  note={note}
                  colorCategories={COLOR_CATEGORIES}
                  onEdit={null}
                  onDelete={null}
                />
              ))}
            </div>
          ) : (
            <p></p>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsLinkedNotesModalOpen(false)}>
            {t('history.note.close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default History;



// import React, { useEffect, useState, useRef } from 'react';
// import { useAuth } from '../services/AuthContext';
// import { useNotes } from "../services/NotesContext";
// import { fetchWithAuth } from '../services/AuthService';
// import { COLOR_CATEGORIES } from "../constants/colorCategories";
// import QuizResponse from './QuizResponse';
// import NoteCard from './NoteCard';
// import { Modal, Button } from 'react-bootstrap';
// import { useTranslation } from 'react-i18next';
// import '../css/Result.css';

// const History = () => {
//   const { t } = useTranslation();
//   const { isUserAuthenticated, authLoading } = useAuth();
//   const { checkAndLoadNotes } = useNotes();
//   const [results, setResults] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   const [isLinkedNotesModalOpen, setIsLinkedNotesModalOpen] = useState(false);
//   const [selectedNotes, setSelectedNotes] = useState([]);
//   const [selectedScan, setSelectedScan] = useState(null);

//   const loadingBuffer = 3;
//   const [visibleCount, setVisibleCount] = useState(loadingBuffer);

//   const containerRef = useRef(null);

//   useEffect(() => {
//     if (authLoading) return;

//     if (!isUserAuthenticated) {
//       setLoading(false);
//       setError("Vous devez être connecté pour voir l'historique.");
//       return;
//     }

//     const loadAllData = async () => {
//       try {
//         //console.log("Get History");
//         await checkAndLoadNotes();

//         const response = await fetchWithAuth('/api/results?source=history');
//         if (!response.ok) throw new Error('Erreur lors du chargement des résultats.');

//         const data = await response.json();
//         setResults(data);
//       } catch (err) {
//         console.error(err);
//         setError("Impossible de charger les résultats.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     loadAllData();
//   }, [isUserAuthenticated, authLoading]);

//   useEffect(() => {
//     const container = containerRef.current;
//     if (!container) return;

//     const handleScroll = () => {
//       // Vérifie si on est proche du bas
//       if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
//         setVisibleCount((prev) => Math.min(prev + loadingBuffer, results.length));
//       }
//     };

//     container.addEventListener('scroll', handleScroll);
//     return () => container.removeEventListener('scroll', handleScroll);
//   }, [results.length]);

//   // --- Fonction appelée depuis QuizResponse ---
//   const handleOpenLinkedNotes = async (scanId, linkedNotes) => {
//     // on peut utiliser le paramètre linkedNotes OU regénérer depuis le contexte :
//     const notesForScan = linkedNotes;
//     setSelectedScan(scanId);
//     setSelectedNotes(notesForScan);
//     setIsLinkedNotesModalOpen(true);
//   };

//   if (loading) return <p>Chargement...</p>;
//   if (authLoading) return <p>Vérification de la session...</p>;
//   if (error) return <p>{error}</p>;
//   if (results.length === 0) return <p>Aucun résultat enregistré.</p>;

//   return (
//     <>
//     <div ref={containerRef} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
//       {results.slice(0, visibleCount).map((result) => {
//         return (
//           <QuizResponse
//             key={result._id}
//             scanId={result._id}
//             scoringValue={result.scoring}
//             answers={result.answers}
//             createdAt={result.createdAt}
//             from="history"
//             onOpenLinkedNotes={handleOpenLinkedNotes}
//           />
//         );
//       })}
//     </div>

//     {/* --- MODAL GLOBALE : Notes liées --- */}
//       <Modal
//         show={isLinkedNotesModalOpen}
//         onHide={() => setIsLinkedNotesModalOpen(false)}
//         centered
//         size="lg"
//       >
//         <Modal.Header>
//           <Modal.Title>{t('history.note.header')}</Modal.Title>
//         </Modal.Header>

//         <Modal.Body>
//           {selectedNotes && selectedNotes.length > 0 ? (
//             <div className="notes-list">
//               {selectedNotes.map((note) => (
//                 <NoteCard
//                   key={note._id}
//                   note={note}
//                   colorCategories={COLOR_CATEGORIES}
//                   onEdit={null}
//                   onDelete={null}
//                 />
//               ))}
//             </div>
//           ) : (
//             <p></p>
//           )}
//         </Modal.Body>

//         <Modal.Footer>
//           <Button variant="secondary" onClick={() => setIsLinkedNotesModalOpen(false)}>
//             {t('history.note.close')}
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   );
// };

// export default History;